import {
  describeSeatLayout,
  normalizeSeatNumber,
  parseSeatFloor,
  resolveSelectedSeatReferences,
} from './seatPreferenceMetadata';
import { getSeatCategory, isPetFriendlySeat, isSeat } from './seats';

const cloneValue = (value, seen = new WeakMap()) => {
  if (!value || typeof value !== 'object') return value;
  if (seen.has(value)) return seen.get(value);

  const clone = Array.isArray(value) ? [] : {};
  seen.set(value, clone);
  Object.keys(value).forEach((key) => {
    clone[key] = cloneValue(value[key], seen);
  });
  return clone;
};

/**
 * Clones ordered seat references so later selection mutations cannot alter transport state.
 * @param {Array<object>} references Seat references.
 * @returns {Array<object>|null} Deep cloned references or null when absent/invalid.
 */
export const cloneSelectedSeatReferences = (references) => {
  if (!Array.isArray(references) || references.length === 0) return null;
  return references.map((reference) => cloneValue(reference));
};

/**
 * Distinguishes passenger seats from adjacent and pet ancillary selections.
 * @param {object} seat Selected seat-like record.
 * @returns {boolean} Whether the record represents a primary passenger seat.
 */
export const isPrimarySeatReference = (seat) =>
  Boolean(seat && !seat.isPickedAsAdjacent && !isPetFriendlySeat(getSeatCategory(seat)));

const identityValue = (value) => {
  if (value === null) return ['null'];
  if (value === undefined) return ['undefined'];
  if (['string', 'boolean'].includes(typeof value)) return [typeof value, value];
  if (typeof value === 'number') {
    return Number.isFinite(value) ? ['number', value] : ['invalid-number'];
  }
  return [typeof value];
};

/**
 * Produces the resolver identity portion used to distinguish modal creation requests.
 * @param {Array<object>} references Seat references.
 * @returns {Array<object>|null} Ordered identity data.
 */
export const getSelectedSeatReferencesIdentity = (references) => {
  if (!Array.isArray(references) || references.length === 0) return null;
  return references.map((reference) => ({
    number: identityValue(reference?.number),
    seatLevel: identityValue(reference?.seatLevel),
    hasSeatLevel: Object.prototype.hasOwnProperty.call(reference || {}, 'seatLevel'),
    tripSlug: identityValue(reference?.tripSlug),
  }));
};

/**
 * Builds the identity of a modal purchase creation request.
 * @param {object} payload Modal orchestration payload.
 * @returns {string} Stable request identity.
 */
export const getQuickPurchasePayloadKey = (payload) =>
  JSON.stringify({
    departureId: payload?.departureId,
    returnId: payload?.returnId,
    passengersParam: payload?.passengersParam || 'A1',
    seatNumbers: payload?.seatNumbers || [],
    seenPrice: payload?.seenPrice,
    selectedSeatReferences: getSelectedSeatReferencesIdentity(payload?.selectedSeatReferences),
  });

/**
 * Resolves explicit or number-only references against the current complete layout.
 * @param {object} params Resolution input.
 * @param {Array} params.layout Current raw layout.
 * @param {string} params.tripSlug Current trip identity.
 * @param {Array<object>} params.references Ordered references.
 * @param {number} params.passengerCount Exact booking count.
 * @param {string} [params.diagramType] Diagram family. Omitted values remain unknown.
 * @param {boolean} [params.showUniformLayout=false] Whether geometry is uniform.
 * @param {boolean} [params.legacyTransport=false] Whether the eventual request is number-only.
 * @returns {Array<object>|null} Ordered current raw seat clones.
 */
export const resolveQuickPurchaseSeatReferences = ({
  layout,
  tripSlug,
  references,
  passengerCount,
  diagramType,
  showUniformLayout = false,
  legacyTransport = false,
}) => {
  if (!Array.isArray(references) || references.length !== passengerCount) return null;

  const descriptors = describeSeatLayout(layout, {
    tripSlug,
    diagramType,
    showUniformLayout,
  });
  const resolved = resolveSelectedSeatReferences(descriptors, references, {
    requireAvailable: true,
  });
  if (!resolved) return null;

  const hasIneligibleCategory = resolved.some(
    ({ seat }) => !isSeat(getSeatCategory(seat)) || isPetFriendlySeat(getSeatCategory(seat)),
  );
  if (hasIneligibleCategory) return null;

  const numberCounts = descriptors.reduce((counts, descriptor) => {
    const number = normalizeSeatNumber(descriptor.seat?.number);
    if (number !== null) counts.set(number, (counts.get(number) || 0) + 1);
    return counts;
  }, new Map());
  const hasUnsupportedIdentity = resolved.some(({ seat, preference }) => {
    const number = normalizeSeatNumber(seat.number);
    if (numberCounts.get(number) === 1) return false;
    if (legacyTransport) return true;

    const rawFloor = parseSeatFloor(seat.seatLevel);
    return rawFloor.status !== 'valid' || rawFloor.value !== preference.floor;
  });
  if (hasUnsupportedIdentity) {
    return null;
  }

  return resolved.map(({ seat }) => cloneValue(seat));
};
